import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AppSmartBanner.css';

const IOS_URL =
  'https://apps.apple.com/us/app/autoplace-al-makina-n%C3%AB-shitje/id6753959493';
const ANDROID_URL =
  'https://play.google.com/store/apps/details?id=al.autoplace.app';

const APP_SCHEME = 'autoplacemobile://';

const BANNER_KEY = 'ap_banner_dismissed';
const POPUP_KEY = 'ap_popup_dismissed';

function getDevice() {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  return { isIOS, isAndroid, isMobile: isIOS || isAndroid };
}

export default function AppSmartBanner() {
  const { t } = useTranslation();
  const { isIOS, isAndroid, isMobile } = getDevice();
  const [showBanner, setShowBanner] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [tryingToOpen, setTryingToOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const storeUrl = isIOS ? IOS_URL : ANDROID_URL;

  useEffect(() => {
    if (!isMobile) return;

    // iOS has the native Safari smart banner — only show custom banner on Android
    if (isAndroid && !localStorage.getItem(BANNER_KEY)) setShowBanner(true);

    if (!localStorage.getItem(POPUP_KEY)) {
      const t = setTimeout(() => setShowPopup(true), 1500);
      return () => clearTimeout(t);
    }
  }, [isMobile]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem(BANNER_KEY, '1');
  };

  const dismissPopup = () => {
    setShowPopup(false);
    localStorage.setItem(POPUP_KEY, '1');
  };

  const handleOpenApp = () => {
    setTryingToOpen(true);

    // Attempt to open the app via URL scheme
    window.location.href = APP_SCHEME;

    // If the app isn't installed the page stays in focus — redirect to store
    timeoutRef.current = setTimeout(() => {
      setTryingToOpen(false);
      window.open(storeUrl, '_blank', 'noopener,noreferrer');
    }, 2500);

    // If the app opens, the page goes hidden — cancel the store redirect
    const onVisibilityChange = () => {
      if (document.hidden && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        setTryingToOpen(false);
        dismissPopup();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange, {
      once: true,
    });
  };

  if (!isMobile) return null;

  return (
    <>
      {/* ── Sticky top banner ── */}
      {showBanner && (
        <div className="ap-banner">
          <button
            className="ap-banner__close"
            onClick={dismissBanner}
            aria-label="Dismiss"
          >
            ×
          </button>

          <img
            className="ap-banner__icon"
            src="/images/logos/AutoPlaceSquare.png"
            alt="AutoPlace"
          />

          <div className="ap-banner__info">
            <span className="ap-banner__name">AutoPlace</span>
            <span className="ap-banner__sub">
              {isIOS ? t('app_banner.on_app_store') : t('app_banner.on_google_play')}
            </span>
          </div>

          <a
            className="ap-banner__btn"
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismissBanner}
          >
            {isIOS ? t('app_banner.get') : t('app_banner.install')}
          </a>
        </div>
      )}

      {/* ── Bottom-sheet popup ── */}
      {showPopup && (
        <div className="ap-popup-overlay" onClick={dismissPopup}>
          <div className="ap-popup" onClick={e => e.stopPropagation()}>
            <button
              className="ap-popup__close"
              onClick={dismissPopup}
              aria-label="Close"
            >
              ×
            </button>

            <img
              className="ap-popup__icon"
              src="/images/logos/AutoPlaceSquare.png"
              alt="AutoPlace"
            />

            <h3 className="ap-popup__title">{t('app_banner.open_in_app')}</h3>
            <p className="ap-popup__desc">
              {t('app_banner.popup_desc')}
            </p>

            <button
              className="ap-popup__open-btn"
              onClick={handleOpenApp}
              disabled={tryingToOpen}
            >
              {tryingToOpen ? t('app_banner.opening') : t('app_banner.open_app')}
            </button>

            <a
              className="ap-popup__store-link"
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {isIOS ? t('app_banner.download_app_store') : t('app_banner.get_google_play')}
            </a>

            <button className="ap-popup__stay-btn" onClick={dismissPopup}>
              {t('app_banner.continue_browser')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
